import type { Lesson, LessonFormData } from '@court-sync/shared';
import {
  formatDateToFormYmd,
  getLessonCoachIds,
  getLessonCourtIds,
} from '@court-sync/shared';

function normalizeTimeForForm(t: string | undefined): string {
  if (!t) return '';
  const s = String(t).trim();
  if (s.length >= 5) return s.slice(0, 5);
  return s;
}

function sortPrivateSessionDates(dates: string[] | undefined): string[] {
  return [...(dates ?? [])].filter(Boolean).sort();
}

function weekdaysFromPrivateSessionDates(dates: string[]): number[] {
  const set = new Set<number>();
  for (const d of dates) {
    const parsed = new Date(`${d}T12:00:00`);
    if (!Number.isNaN(parsed.getTime())) set.add(parsed.getDay());
  }
  return [...set].sort((a, b) => a - b);
}

function lessonDateToFormString(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date) return formatDateToFormYmd(value);
  return '';
}

/** Term-free standalone private lesson defaults for Calendar quick create. */
export function buildQuickCreateLessonFormValues(prefillDate?: Date): LessonFormData {
  const sessionDates = prefillDate ? [formatDateToFormYmd(prefillDate)] : [];
  return {
    lessonType: 'private',
    daysOfWeek: prefillDate ? [prefillDate.getDay()] : sessionDates.length ? weekdaysFromPrivateSessionDates(sessionDates) : [1],
    maxCapacity: 1,
    price: 50,
    campDayPrice: 45,
    campFullPrice: 200,
    campSessionDates: [],
    isActive: true,
    isFeatured: false,
    termIds: [],
    privateSessionDates: sessionDates,
    startDate: sessionDates[0] ?? '',
    endDate: '',
    numberOfSessions: sessionDates.length > 0 ? sessionDates.length : undefined,
    pricingType: 'total',
    courtIds: [],
    coachIds: [],
    name: '',
    description: '',
    skillLevel: 'any',
    ageGroup: 'all',
    startTime: '',
    endTime: '',
    locationId: '',
    imageUrl: '',
    programId: null,
    academySportId: undefined,
  };
}

/** Only standalone private lessons can be duplicated from Calendar quick create. */
export function canQuickDuplicateLesson(
  lesson: Pick<Lesson, 'lessonType' | 'termId'>
): boolean {
  return lesson.lessonType === 'private' && !lesson.termId;
}

/** Prefill quick-create form from an existing standalone lesson. */
export function buildDuplicateLessonFormValues(
  source: Lesson,
  prefillDate?: Date
): LessonFormData {
  const existingDates = sortPrivateSessionDates(
    (source as Lesson & { privateSessionDates?: string[] }).privateSessionDates
  );
  const fallbackDate = lessonDateToFormString(source.startDate);
  const sessionDates = prefillDate
    ? [formatDateToFormYmd(prefillDate)]
    : existingDates.length > 0
      ? existingDates
      : fallbackDate
        ? [fallbackDate]
        : [];

  const sourceSessionCount =
    sessionDates.length ||
    source.numberOfSessions ||
    (existingDates.length > 0 ? existingDates.length : 1);
  const totalPrice = source.price ?? 50;
  const perSessionPrice =
    source.pricePerSession != null && source.pricePerSession >= 0
      ? source.pricePerSession
      : sourceSessionCount > 0
        ? totalPrice / sourceSessionCount
        : totalPrice;

  const baseName = source.name.replace(/\s*\(copy\)$/i, '').trim();

  return {
    ...buildQuickCreateLessonFormValues(prefillDate),
    name: `${baseName} (copy)`,
    description: source.description ?? '',
    courtIds: getLessonCourtIds(source),
    coachIds: getLessonCoachIds(source),
    startTime: normalizeTimeForForm(source.startTime),
    endTime: normalizeTimeForForm(source.endTime),
    maxCapacity:
      source.maxCapacity != null && source.maxCapacity >= 1 ? source.maxCapacity : 1,
    price: perSessionPrice,
    skillLevel: source.skillLevel ?? 'any',
    ageGroup: source.ageGroup ?? 'all',
    ageGroupId: source.ageGroupId ?? '',
    programId: source.programId ?? null,
    academySportId: source.academySportId ?? undefined,
    locationId: source.locationId ?? '',
    isActive: source.isActive ?? true,
    isFeatured: source.isFeatured ?? false,
    privateSessionDates: sessionDates,
    startDate: sessionDates[0] ?? '',
    numberOfSessions: sessionDates.length > 0 ? sessionDates.length : undefined,
    daysOfWeek:
      sessionDates.length > 0
        ? weekdaysFromPrivateSessionDates(sessionDates)
        : prefillDate
          ? [prefillDate.getDay()]
          : [1],
  };
}

export { sortPrivateSessionDates, weekdaysFromPrivateSessionDates };
